import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Loader2, Download, Save, ArrowUpRight, ArrowDownRight, Minus, Info } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { fmt$, fmt$2, fmtPct } from "@/lib/format";
import { incomeBasis } from "@/lib/incomePlanner";
import { buildCsv, downloadBlob } from "@/lib/csv";
import OptimizerUpgradePreview from "@/components/holdings/OptimizerUpgradePreview";

const GOALS = [
  { key: "yield", label: "Max income" },
  { key: "cagr", label: "Max growth" },
  { key: "balanced", label: "Balanced" },
];

// Pro: AI reweighting of the user's synced holdings (same tickers, ±N pts per position) + trade list.
export default function HoldingsOptimizerCard({ accounts, defaultScope = "all" }) {
  const { user } = useAuth();
  const { sub } = useSubscription() || {};
  const isPro = !!user?.is_admin || sub?.plan === "pro";
  const nav = useNavigate();
  const [scope, setScope] = useState(defaultScope);
  const [goal, setGoal] = useState("balanced");
  const [maxChange, setMaxChange] = useState(10);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isPro) return;
    api.get("/holdings/optimize/last", { params: scope === "all" ? {} : { account_id: scope } })
      .then((r) => setResult(r.data.result || null)).catch(() => {});
  }, [isPro, scope]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/holdings/optimize", {
        account_id: scope === "all" ? null : scope, goal, max_change_pts: Number(maxChange) || 10,
      });
      setResult(r.data);
      toast.success("Optimization ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Optimizer failed");
    } finally { setBusy(false); }
  };

  const saveAsPortfolio = async () => {
    setSaving(true);
    try {
      const label = GOALS.find((g) => g.key === result.goal)?.label || "Optimized";
      const r = await api.post("/portfolios", {
        name: `Optimized holdings · ${label}`,
        description: `AI reweighting of synced holdings (±${result.max_change_pts} pts) on ${result.created_at.slice(0, 10)}`,
        holdings: result.proposed.weights,
        initial_investment: Math.round(result.total_value),
        start_date: result.start_date,
        end_date: result.end_date,
        drip: true,
        additional_contribution: 0,
        contribution_frequency: "monthly",
      });
      toast.success("Saved as a new portfolio");
      nav(`/portfolio/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save portfolio");
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-primary/30" data-testid="holdings-optimizer-card">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-primary flex items-center gap-2">
              <Wand2 className="w-3.5 h-3.5" /> AI Holdings Optimizer
              <span className="rounded-full bg-secondary/15 text-secondary px-2 py-0.5 normal-case tracking-normal">Pro</span>
            </div>
            <h3 className="serif text-2xl mt-2">Optimize what you already own</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Reweights your synced positions toward a goal, keeps every change within your limit, and gives you a rebalancing list. DPP never places trades.
            </p>
          </div>
        </div>
        {!isPro ? <OptimizerUpgradePreview onUpgrade={() => nav("/pricing")} /> : (
          <>
            <Controls {...{ accounts, scope, setScope, goal, setGoal, maxChange, setMaxChange, busy, run }} />
            {result && <OptimizerResult result={result} onSave={saveAsPortfolio} saving={saving} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Controls({ accounts, scope, setScope, goal, setGoal, maxChange, setMaxChange, busy, run }) {
  return (
    <div className="grid sm:grid-cols-[1.3fr_1.4fr_0.8fr_auto] gap-3 items-end">
      <div>
        <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Accounts</Label>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="mt-1" data-testid="holdings-optimizer-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts combined</SelectItem>
            {(accounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Goal</Label>
        <div className="mt-1 flex rounded-md border border-border p-0.5" role="radiogroup">
          {GOALS.map((g) => (
            <button key={g.key} type="button" onClick={() => setGoal(g.key)} data-testid={`holdings-optimizer-goal-${g.key}`}
              className={`flex-1 rounded px-2 py-1.5 text-xs font-semibold transition-colors ${goal === g.key ? "bg-primary text-primary-foreground" : "text-primary hover:bg-primary/10"}`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label htmlFor="ho-max" className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Max change ± pts</Label>
        <Input id="ho-max" type="number" min={1} max={50} step={1} value={maxChange} onChange={(e) => setMaxChange(e.target.value)} className="mt-1 mono" data-testid="holdings-optimizer-max-change" />
      </div>
      <Button className="rounded-full" onClick={run} disabled={busy} data-testid="holdings-optimizer-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Optimizing…</> : <><Wand2 className="w-4 h-4 mr-2" />Optimize</>}
      </Button>
    </div>
  );
}

function OptimizerResult({ result, onSave, saving }) {
  const cur = result.current.summary;
  const pro = result.proposed.summary;
  const rows = [
    ["Monthly income", fmt$2(incomeBasis(cur).monthly), fmt$2(incomeBasis(pro).monthly), incomeBasis(pro).monthly - incomeBasis(cur).monthly],
    ["Current yield", fmtPct(incomeBasis(cur).currentYield), fmtPct(incomeBasis(pro).currentYield), incomeBasis(pro).currentYield - incomeBasis(cur).currentYield],
    ["CAGR", fmtPct(cur.cagr_pct), fmtPct(pro.cagr_pct), pro.cagr_pct - cur.cagr_pct],
    ["Total return", fmtPct(cur.total_return_pct), fmtPct(pro.total_return_pct), pro.total_return_pct - cur.total_return_pct],
  ];
  return (
    <div className="space-y-4" data-testid="holdings-optimizer-result">
      <div className="mono text-[11px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        <span>{fmt$(result.total_value)} across {result.trades.length} positions</span>
        <span>Backtest {result.start_date} → {result.end_date}</span>
        {result.limited_by?.length > 0 && <span>· period limited by {result.limited_by.join(", ")} history</span>}
        <span>Run {new Date(result.created_at).toLocaleString()}</span>
      </div>
      {result.rationale && <p className="text-sm leading-relaxed" data-testid="holdings-optimizer-rationale">{result.rationale}</p>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {rows.map(([k, a, b, d]) => (
          <div key={k} className="rounded-md border border-border p-3" data-testid={`holdings-optimizer-metric-${k.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
            <div className="mono text-sm mt-1 flex flex-wrap items-baseline gap-x-1.5"><span className="whitespace-nowrap">{a}</span> <span className="text-muted-foreground">→</span> <b className={`whitespace-nowrap ${d >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{b}</b></div>
          </div>
        ))}
      </div>

      <div className="flex justify-end -mb-2">
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => exportTradesCsv(result)} data-testid="holdings-optimizer-export-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export trade list (CSV)
        </Button>
      </div>
      <TradeTable trades={result.trades} />

      {result.excluded?.length > 0 && (
        <div className="text-xs text-muted-foreground flex gap-2" data-testid="holdings-optimizer-excluded">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Not included: {result.excluded.map((x) => `${x.ticker} (${x.reason})`).join("; ")}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <p className="text-[11px] text-muted-foreground max-w-xl">
          Approximate trades at today&apos;s prices (≈ {fmt$(result.turnover_value)} turnover). Consider taxes, fees and wash-sale rules before trading.
          Educational only — not financial advice.
        </p>
        <Button variant="outline" className="rounded-full" onClick={onSave} disabled={saving} data-testid="holdings-optimizer-save-portfolio">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save as new portfolio
        </Button>
      </div>
    </div>
  );
}

// Older saved results lack share fields — derive them from value / price.
const sh = (t) => {
  const cur = t.current_shares ?? (t.price > 0 ? t.current_value / t.price : 0);
  const target = t.target_shares ?? (t.price > 0 ? cur + t.delta_value / t.price : cur);
  return { cur, target, change: target - cur };
};
// Nearest whole share; a sell never exceeds the whole shares currently held.
const wholeShares = (t) => {
  const { cur, change } = sh(t);
  const n = Math.round(Math.abs(change));
  return change < 0 ? Math.min(n, Math.floor(cur)) : n;
};
const fmtSh = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function exportTradesCsv(result) {
  const r2 = (n) => Math.round(Number(n) * 100) / 100;
  const headers = ["Ticker", "Name", "Pays", "Action", "Whole shares", "Current shares", "Proposed shares", "Share change",
    "Current weight %", "Proposed weight %", "Approx. price", "Current value", "Target value", "Trade amount"];
  const rows = result.trades.map((t) => {
    const { cur, target, change } = sh(t);
    return [t.ticker, t.name || "", t.payout_cadence || "", t.action.toUpperCase(), t.action === "hold" ? 0 : wholeShares(t),
      r2(cur), r2(target), r2(change), t.current_weight, t.proposed_weight, t.price, r2(t.current_value), r2(t.target_value), r2(t.delta_value)];
  });
  const csv = buildCsv(headers, rows)
    + `\n\nGoal,${result.goal}\nMax change (pts),${result.max_change_pts}\nTotal value,${r2(result.total_value)}\nTurnover,${r2(result.turnover_value)}\nGenerated,${result.created_at}\n`
    + "Approximate trades at the prices shown. Educational only - not financial advice.\n";
  downloadBlob(csv, `dpp-optimizer-trades-${result.goal}-${result.created_at.slice(0, 10)}.csv`);
}

function TradeTable({ trades }) {
  const icon = { buy: <ArrowUpRight className="w-3.5 h-3.5" />, sell: <ArrowDownRight className="w-3.5 h-3.5" />, hold: <Minus className="w-3.5 h-3.5" /> };
  const tone = { buy: "text-emerald-700 bg-emerald-50", sell: "text-rose-700 bg-rose-50", hold: "text-muted-foreground bg-muted" };
  const buys = trades.filter((t) => t.action === "buy").reduce((a, t) => a + t.delta_value, 0);
  const sells = -trades.filter((t) => t.action === "sell").reduce((a, t) => a + t.delta_value, 0);
  const nBuy = trades.filter((t) => t.action === "buy").length;
  const nSell = trades.filter((t) => t.action === "sell").length;
  return (
    <div className="overflow-x-auto rounded-md border border-border" data-testid="holdings-optimizer-trades">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="mono text-[10px] uppercase tracking-widest text-muted-foreground text-left">
            <th className="px-2.5 py-2 sticky left-0 z-10 bg-muted">Ticker</th><th className="px-2.5 py-2">Pays</th><th className="px-2.5 py-2 text-right">Weight</th>
            <th className="px-2 py-2">Action</th><th className="px-2.5 py-2 text-right">Amount</th><th className="px-2.5 py-2 text-right">Shares now → new</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <tr key={t.ticker} className="border-t border-border" data-testid={`holdings-optimizer-trade-${t.ticker}`}>
              <td className="px-2.5 py-2 sticky left-0 z-10 bg-card shadow-[1px_0_0_hsl(var(--border))]"><div className="font-semibold">{t.ticker}</div><div className="text-[11px] text-muted-foreground truncate max-w-[110px] sm:max-w-[150px]">{t.name}</div></td>
              <td className="px-2.5 py-2 text-xs whitespace-nowrap" data-testid={`holdings-optimizer-pays-${t.ticker}`}>{t.payout_cadence || <span className="text-muted-foreground">—</span>}</td>
              <td className="px-2.5 py-2 text-right mono whitespace-nowrap">{t.current_weight.toFixed(1)}% → <b>{t.proposed_weight.toFixed(1)}%</b></td>
              <td className="px-2.5 py-2"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold uppercase whitespace-nowrap ${tone[t.action]}`} data-testid={`holdings-optimizer-action-${t.ticker}`}>{icon[t.action]}{t.action}{t.action !== "hold" && <span className="mono normal-case">{wholeShares(t)} sh</span>}</span></td>
              <td className="px-2.5 py-2 text-right mono whitespace-nowrap">{t.action === "hold" ? "—" : fmt$(Math.abs(t.delta_value))}</td>
              <td className="px-2.5 py-2 text-right mono whitespace-nowrap" data-testid={`holdings-optimizer-shares-${t.ticker}`}>
                <div>{fmtSh(sh(t).cur)} → <b>{fmtSh(sh(t).target)}</b></div>
                <div className={`text-xs ${sh(t).change > 0 ? "text-emerald-700" : sh(t).change < 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                  {sh(t).change > 0 ? "+" : sh(t).change < 0 ? "−" : "±"}{fmtSh(Math.abs(sh(t).change))} sh
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border bg-muted/30 text-xs" data-testid="holdings-optimizer-trades-total">
            <td className="px-2.5 py-2 font-semibold sticky left-0 z-10 bg-muted">Totals</td><td colSpan={3} />
            <td className="px-2.5 py-2 text-right mono whitespace-nowrap">
              <div className="text-emerald-700">+{fmt$(buys)}</div><div className="text-rose-700">−{fmt$(sells)}</div>
            </td>
            <td className="px-2.5 py-2 text-right mono whitespace-nowrap">
              <span className="text-emerald-700">{nBuy} buy</span> · <span className="text-rose-700">{nSell} sell</span>{trades.length - nBuy - nSell > 0 && ` · ${trades.length - nBuy - nSell} hold`}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
